import type { UserVariables } from "components/shared/AgreementModal";
import ModalBase from "components/shared/ModalBase";
import StyledButton from "components/shared/StyledButton";
import { Body, SmallBody } from "helpers/TypographyComponents";
import { useTranslation } from "react-i18next";
import { revertAgreementToDraft, updateAgFetchStatus } from "store/agreementsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import { useAgreementPageContext } from "../context/StateProvider";

interface IRevertToDraftModal {
    open: boolean;
    onClose: () => void;
    setUserVariables: (userVariables: UserVariables) => void;
}
/**
 * RevertAgreement to draft modal
 */
const RevertToDraftModal = ({ open, onClose, setUserVariables }: IRevertToDraftModal) => {
    const { agreement } = useAgreementPageContext();
    const { revertToDraftStatus } = useAppSelector((state) => state.agreements.agFetchStatus);
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const handleRevertToDraft = async () => {
        if (!agreement) return;
        updateAgFetchStatus("revertToDraftStatus", "pending");
        try {
            const response = await dispatch(revertAgreementToDraft({ agreementId: +agreement.id }));
            if (response.meta.requestStatus === "fulfilled") {
                setUserVariables(response.payload.agreement.userVariables);
            }
        } finally {
            updateAgFetchStatus("revertToDraftStatus", "idle");
            onClose();
        }
    };

    return (
        <ModalBase open={open} onClose={onClose} modalTitle={t("ContractValidationEdit.RevertToDraft")}>
            <div>
                <Body> {t("ContractValidationEdit.AreYouSureToRevertToDraft")}</Body>
                <div className="mt-4!">
                    <SmallBody color="info">- {t("ContractValidationEdit.ResetToInitialState")}</SmallBody>
                    <SmallBody color="info">
                        - {t("ContractValidationEdit.ValidatorsWillNeedToValidateAgain")}
                    </SmallBody>
                    <SmallBody color="info">- {t("ContractValidationEdit.SignersWillNeedToSignAgain")}</SmallBody>
                </div>
                <div className="flex gap-2 mt-4!">
                    <StyledButton fullWidth buttonType="secondary" onClick={onClose}>
                        {t("ContractValidationEdit.NoCancel")}
                    </StyledButton>
                    <StyledButton
                        buttonType="primary"
                        onClick={handleRevertToDraft}
                        loading={revertToDraftStatus === "pending"}
                        fullWidth
                    >
                        {t("ContractValidationEdit.YesValidate")}
                    </StyledButton>
                </div>
            </div>
        </ModalBase>
    );
};

export default RevertToDraftModal;
